import type { MessageRouter } from "@pdfking/functionpool"
import type { Page, PageNumber } from "@pdfking/renderer-api"

import {
  ArrayToIterable,
  type AsyncSequence,
  type Atom,
  AtomHash,
  First,
  GuidToString,
  Identity,
  IsArray,
  MapAsync,
  MapAsyncConcurrent,
  Map as MapSync,
  MethodDispatcher,
  type Partial,
  Peek,
  Range,
  type StringGUID,
  UUID,
  Values,
  Void,
} from "@pdfking/tfunc"

import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine, TaskIDOptions } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { RemoteFileChunk } from "../types/files/cached/RemoteFileChunk"
import type { SinglePageImage } from "../types/files/cached/SinglePageImage"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { FilePlottingContext } from "../types/tasks/options/TaskPlotOptions"
import type { TaskOptions } from "../types/tasks/TaskOptions"

import { CreateRemoteFile } from "../files/CreateRemoteFile"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { FileContentGUID } from "../methods/FileContentGUID"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TaskName } from "../types/tasks/TaskName"
import { CreatePdfEngineDefaults } from "./CreatePdfEngineDefaults"

const { ceil, max, min } = Math

const make_task_id = (t: TaskName, ...args: Atom[]): Promise<StringGUID> =>
  AtomHash([t, ...args]).then(GuidToString)

// Top-level message queue router with post-processing
const ForwardTask = (
  router: MessageRouter<APIRequest, APIResponse>,
  options: TaskOptions,
  ...input: AsyncSequence<APIRequest>[]
): AsyncGenerator<VirtualFile> => {
  const responses = router(ArrayToIterable([options], ...input))
  return MapAsync(response => (response as TaskResult).content, responses)
}

export const CreateEngineVirtual = (
  config: Partial<PdfEngineOptions>,
  router: MessageRouter<APIRequest, APIResponse>
): PdfEngine => {
  const options = CreatePdfEngineDefaults(config)

  const mapSinglePage = async function* <T>(
    processor: (page: SinglePagePdf) => AsyncSequence<T>,
    files: AsyncSequence<VirtualFile>
  ): AsyncGenerator<T> {
    for await (const file of files) {
      for await (const page of SplitPdf(file)) {
        yield* processor(page)
      }
    }
  }

  const innerSplit = async function* (
    source: GeneratedFile | RemoteFile,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<SinglePagePdf> {
    const max_pages = options.max_split_pages

    // Closure to run split operation for a given page range
    const runSplit = async function* (
      start_page: PageNumber,
      end_page: PageNumber
    ): AsyncGenerator<SinglePagePdf> {
      yield* ForwardTask(router, {
        cache_evict,
        end_page,
        enum: TaskName.TASK_SPLIT,
        options,
        source,
        start_page,
        task_id: await make_task_id(
          TaskName.TASK_SPLIT,
          task_id ?? FileContentGUID(source),
          start_page,
          end_page
        ),
      }) as AsyncGenerator<SinglePagePdf>
    }

    // First batch: pages 1 to max_split_pages
    const { stream, value: first_page } = await Peek(
      runSplit(1 as PageNumber, max_pages)
    )

    if (first_page) {
      // Compute remaining batches
      const batch_count = ceil((first_page.page_count - max_pages) / max_pages)

      if (batch_count > 0) {
        // Generate array of generators for remaining batches
        const remaining_batches = MapSync(batch_index => {
          const start_page = (max_pages +
            batch_index * max_pages +
            1) as PageNumber
          const end_page = min(
            start_page + max_pages - 1,
            first_page.page_count
          ) as PageNumber

          return runSplit(start_page, end_page)
        }, Range(batch_count))

        yield* MapAsyncConcurrent(Identity, stream, ...remaining_batches)
      } else {
        yield* stream
      }
    }
  }

  const SplitPdf = (
    file: VirtualFile,
    options?: TaskIDOptions
  ): AsyncGenerator<SinglePagePdf> => {
    const dispatcher = MethodDispatcher<
      [VirtualFile],
      AsyncGenerator<SinglePagePdf>
    >({
      [VirtualFileType.GENERATED_FILE]: p => innerSplit(p, options),
      [VirtualFileType.MULTI_PAGE_PDF]: p => ArrayToIterable(p.pages),
      [VirtualFileType.REMOTE_FILE]: p => innerSplit(p, options),
      [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p.source]),
      [VirtualFileType.SINGLE_PAGE_PDF]: p => ArrayToIterable([p]),
      [VirtualFileType.ZIP_FILE]: p => mapSinglePage(p => [p], Values(p.files)),
    })
    return dispatcher(file)
  }

  const rasterizeSingle = async function* (
    source: SinglePagePdf,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<SinglePageImage> {
    yield* ForwardTask(router, {
      cache_evict,
      enum: TaskName.TASK_RASTERIZE,
      options,
      source,
      task_id: await make_task_id(
        TaskName.TASK_RASTERIZE,
        task_id ?? FileContentGUID(source)
      ),
    }) as AsyncGenerator<SinglePageImage>
  }

  const RasterizePdf = (
    file: VirtualFile,
    options?: TaskIDOptions
  ): AsyncGenerator<SinglePageImage> => {
    const dispatcher = MethodDispatcher<
      [VirtualFile],
      AsyncGenerator<SinglePageImage>
    >(
      {
        [VirtualFileType.MULTI_PAGE_PDF]: p =>
          mapSinglePage(p => rasterizeSingle(p, options), p.pages),
        [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p]),
        [VirtualFileType.SINGLE_PAGE_PDF]: p => rasterizeSingle(p, options),
      },
      p => mapSinglePage(p => rasterizeSingle(p, options), [p])
    )
    return dispatcher(file)
  }

  const MergePdf = async (
    files: AsyncSequence<[PageNumber, PageNumber, SinglePagePdf] | VirtualFile>,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): Promise<GeneratedFile> => {
    let page_index = 0 as PageNumber

    const pages = async function* (): AsyncGenerator<
      [PageNumber, PageNumber, SinglePagePdf]
    > {
      for await (const page of files) {
        if (IsArray(page)) {
          yield page as [PageNumber, PageNumber, SinglePagePdf]
          page_index = max(page_index, page[1]) as PageNumber
        } else {
          const page_count = page_index
          for await (const pdf of SplitPdf(page)) {
            page_index = (page_index + 1) as PageNumber
            yield [page_index, (page_count + pdf.page_count) as PageNumber, pdf]
          }
        }
      }
    }

    return First(
      ForwardTask(
        router,
        {
          cache_evict,
          enum: TaskName.TASK_MERGE,
          options,
          task_id: await make_task_id(TaskName.TASK_MERGE, task_id ?? UUID()),
        },
        MapAsync(
          ([page_number, page_count, content]) => ({
            content,
            enum: TaskName.CONTENT_PAGED,
            page_count,
            page_number,
          }),
          pages()
        )
      )
    ) as Promise<GeneratedFile>
  }

  const ZipFiles = async (
    files: AsyncSequence<[string, VirtualFile]>,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): Promise<GeneratedFile> =>
    First(
      ForwardTask(
        router,
        {
          cache_evict,
          enum: TaskName.TASK_ZIP,
          options,
          task_id: await make_task_id(TaskName.TASK_ZIP, task_id ?? UUID()),
        },
        MapAsync(
          ([name, file]) => ({
            content: file,
            enum: TaskName.CONTENT_FILE,
            name,
          }),
          files
        )
      )
    ) as Promise<GeneratedFile>

  const PlotPdf = async (
    page: Page,
    context: FilePlottingContext = {},
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): Promise<SinglePagePdf> =>
    First(
      ForwardTask(router, {
        cache_evict,
        context,
        enum: TaskName.TASK_PLOT,
        options,
        page,
        task_id: await make_task_id(TaskName.TASK_PLOT, task_id ?? UUID()),
      })
    ) as Promise<SinglePagePdf>

  const DownloadFile = async function* (
    source: RemoteFile,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<RemoteFileChunk> {
    yield* ForwardTask(router, {
      cache_evict,
      enum: TaskName.TASK_DOWNLOAD,
      options,
      source,
      task_id: await make_task_id(
        TaskName.TASK_DOWNLOAD,
        task_id ?? FileContentGUID(source)
      ),
    }) as AsyncGenerator<RemoteFileChunk>
  }

  const engine: PdfEngine = {
    config: options,
    CreateRemoteFile,
    DownloadFile,
    GenerateFile: (stream, content_type) =>
      GenerateFile(engine, stream, content_type, Void),
    MergePdf,
    PlotPdf,
    RasterizePdf,
    RequestBlob: (file, emit) => RequestBlob(engine, file, emit),
    SplitPdf,
    ZipFiles,
  }

  return engine
}
